import type {
  Category,
  ICartProduct,
  ISearchProduct,
  PBMInfo,
  PBMProgram,
  Product,
  ProductPaginator,
} from '@/types';
import { API_ENDPOINTS } from '@/framework/client/api-endpoints';
import client from '@/framework/client';
import { HttpClient } from '@/framework/client/http-client';
import { generateCartItem } from '@/store/quick-cart/generate-cart-item';
import {
  getProductsByIds,
  getProductsBySkus,
} from '@/lib/services/typesense/client/server-client';

import type { ServerCartLine } from './cart-sync.types';

/** Rebuild `PBMProgram` from sync metadata so checkout can send middleware id + slug to the API. */
function pbmProgramFromCartMetadata(raw: unknown): PBMProgram | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const p = raw as Record<string, unknown>;
  const progId = Number(p.id);
  if (!Number.isFinite(progId) || progId < 1) return undefined;

  const mwRaw = p.middleware;
  if (!mwRaw || typeof mwRaw !== 'object') return undefined;
  const mw = mwRaw as Record<string, unknown>;
  const mid = Number(mw.id);
  const slug = String(mw.slug ?? '').trim();
  if (!Number.isFinite(mid) || mid < 1 || !slug) return undefined;

  return {
    id: progId,
    name: String(p.name ?? ''),
    register_url:
      typeof p.register_url === 'string' ? p.register_url : '',
    middleware: { id: mid, slug },
  };
}

function firstCategoryName(doc: Record<string, unknown>): string {
  const c = doc.categories;
  if (typeof doc.category === 'string' && doc.category) return doc.category;
  if (!Array.isArray(c) || c.length === 0) return '';
  const first = c[0];
  if (typeof first === 'string') return first;
  if (first && typeof first === 'object' && 'name' in first) {
    return String((first as { name?: string }).name ?? '');
  }
  return '';
}

function typesenseDocToSearchProduct(
  doc: Record<string, unknown>,
  language: string,
): ISearchProduct & { quantity?: number; in_stock?: number } {
  const sku = String(doc.sku ?? doc.id ?? '');
  const stock = Number(
    doc.quantity ?? doc.in_stock ?? doc.stock ?? 0,
  );
  return {
    id: sku,
    sku,
    product_id: Number(doc.product_id ?? doc.id ?? 0),
    name: String(doc.name ?? ''),
    slug: String(doc.slug ?? ''),
    price: Number(doc.price ?? 0),
    sale_price: Number(doc.sale_price ?? doc.price ?? 0),
    manufacturer: String(doc.manufacturer ?? doc.brand ?? ''),
    brand: String(doc.brand ?? doc.manufacturer ?? ''),
    category: firstCategoryName(doc),
    categories: (doc.categories as Category[]) ?? undefined,
    image: doc.image as ISearchProduct['image'],
    gallery: doc.gallery as ISearchProduct['gallery'],
    quantity: stock,
    in_stock: stock,
    product_prices: (doc.product_prices as ISearchProduct['product_prices']) ?? [],
    unit: doc.unit,
    type_id: doc.type_id,
    type_ids: (() => {
      if (Array.isArray(doc.type_ids) && doc.type_ids.length) {
        return doc.type_ids as (string | number)[];
      }
      if (Array.isArray(doc.types_id) && doc.types_id.length) {
        return doc.types_id as (string | number)[];
      }
      if (Array.isArray(doc.types) && doc.types.length) {
        return (doc.types as { id?: string | number }[])
          .map((t) => t?.id)
          .filter((x) => x != null) as (string | number)[];
      }
      return undefined;
    })(),
    types_id: Array.isArray(doc.types_id)
      ? (doc.types_id as number[]).map((n) => Number(n)).filter((n) => Number.isFinite(n))
      : undefined,
    types: doc.types as ISearchProduct['types'],
    language,
    characteristics: doc.characteristics as ISearchProduct['characteristics'],
    is_promotional_pack: doc.is_promotional_pack as boolean | undefined,
    promotional_pack_quantity: doc.promotional_pack_quantity as number | undefined,
    promotional_pack_unit_price: doc.promotional_pack_unit_price as number | undefined,
    cashback_price: doc.cashback_price as number | undefined,
    in_flash_sale: doc.in_flash_sale as boolean | undefined,
    shop: doc.shop,
    product_type: doc.product_type as string | undefined,
  };
}

function applyServerLineOverrides(
  cartItem: ICartProduct,
  line: ServerCartLine,
): ICartProduct {
  let pbm: ICartProduct['pbm_info'] = cartItem.pbm_info;
  let pbmUnit: number | undefined;
  if (line.metadata && typeof line.metadata === 'object') {
    const m = line.metadata as Record<string, unknown>;
    if (m.flag === true) {
      const programFromMeta = pbmProgramFromCartMetadata(m.program);
      const legacyProgram =
        !programFromMeta && m.program && typeof m.program === 'object'
          ? (m.program as PBMInfo['program'])
          : undefined;
      pbm = {
        flag: true,
        sale_price_id: Number(m.sale_price_id) || 0,
        cpf: String(m.cpf ?? ''),
        quantity: Number(m.quantity) || 0,
        program: programFromMeta ?? legacyProgram,
      };
      const raw = m.pbm_sale_price;
      if (typeof raw === 'number' && Number.isFinite(raw) && raw >= 0) {
        pbmUnit = raw;
      }
    }
  }
  const pricePatch =
    pbmUnit !== undefined
      ? {
          price: pbmUnit,
          sale_price: pbmUnit,
          calculated_sale_price: pbmUnit,
        }
      : {};
  return {
    ...cartItem,
    ...pricePatch,
    id: line.sku,
    sku: line.sku,
    product_id: line.product_id,
    quantity: line.quantity,
    cart_line_updated_at: line.last_modified_at,
    pbm_info: pbm,
    ...(line.metadata &&
    typeof line.metadata === 'object' &&
    (line.metadata as Record<string, unknown>).from_prescription === true
      ? { from_prescription: true }
      : {}),
  };
}

function parseVariationIdFromSku(sku: string): { variationId: string } | null {
  const i = sku.indexOf('.');
  if (i < 0) return null;
  return { variationId: sku.slice(i + 1) };
}

function buildCartItemFromTypesenseDoc(
  doc: Record<string, unknown>,
  line: ServerCartLine,
  language: string,
): ICartProduct {
  const searchShape = typesenseDocToSearchProduct(doc, language);
  const varHint = parseVariationIdFromSku(line.sku);
  let cartItem: ICartProduct;

  const variations = doc.variations;
  if (varHint && Array.isArray(variations)) {
    const variation = variations.find(
      (v: { id?: string | number }) => String(v?.id) === varHint.variationId,
    ) as Parameters<typeof generateCartItem>[1] | undefined;
    cartItem = variation
      ? generateCartItem(searchShape, variation)
      : generateCartItem(searchShape);
  } else {
    cartItem = generateCartItem(searchShape);
  }

  return applyServerLineOverrides(cartItem, line);
}

async function hydrateFromTypesense(
  line: ServerCartLine,
  language: string,
): Promise<ICartProduct | null> {
  try {
    const bySku = await getProductsBySkus([line.sku]);
    let doc = bySku[0] as Record<string, unknown> | undefined;
    if (!doc) {
      const byPid = await getProductsByIds([line.product_id]);
      doc =
        (byPid.find(
          (d: Record<string, unknown>) => String(d.sku ?? d.id) === line.sku,
        ) as Record<string, unknown> | undefined) ??
        (byPid[0] as Record<string, unknown> | undefined);
    }
    if (!doc) return null;
    return buildCartItemFromTypesenseDoc(doc, line, language);
  } catch {
    return null;
  }
}

async function mergeChannelPricesIntoProduct(
  product: Product,
  productId: number,
): Promise<Product> {
  try {
    const prices = await client.productPrice.get({
      search: `product.id:${productId}`,
      include: 'price_channel',
    });
    const primary = prices?.find(
      (p: { price_channel_id?: number }) => p.price_channel_id === 1,
    );
    if (!primary) return product;
    return {
      ...product,
      price: primary.price ?? product.price,
      sale_price: primary.sale_price ?? product.sale_price,
      prices: prices ?? product.prices,
    };
  } catch {
    return product;
  }
}

async function hydrateFromRestCatalog(
  line: ServerCartLine,
  language: string,
): Promise<ICartProduct | null> {
  try {
    const search = HttpClient.formatSearchParams({
      'product.id': String(line.product_id),
      'product_status.name': 'publish',
    } as Record<string, string>);

    const res = await HttpClient.get<ProductPaginator>(API_ENDPOINTS.PRODUCTS, {
      searchJoin: 'and',
      search,
      limit: 1,
      page: 1,
      language,
      with: 'type;types;product_status;variations;variation_options;categories;shop;manufacturer',
    });

    let product = res?.data?.[0] as Product | undefined;
    if (!product) return null;

    product = await mergeChannelPricesIntoProduct(product, line.product_id);

    const searchShape = {
      ...product,
      product_prices: (product.prices ??
        []) as ISearchProduct['product_prices'],
      brand:
        typeof product.manufacturer === 'object' && product.manufacturer
          ? ((product.manufacturer as { name?: string }).name ?? '')
          : '',
      category: product.categories?.[0]?.name ?? '',
      quantity: line.quantity,
      language,
    } as unknown as ISearchProduct;

    const varHint = parseVariationIdFromSku(line.sku);
    let cartItem: ICartProduct;

    if (varHint && Array.isArray(product.variations)) {
      const variation = product.variations.find(
        (v: { id?: string | number }) => String(v?.id) === varHint.variationId,
      ) as Parameters<typeof generateCartItem>[1] | undefined;
      cartItem = variation
        ? generateCartItem(searchShape, variation)
        : generateCartItem(searchShape);
    } else {
      cartItem = generateCartItem(searchShape);
    }

    return applyServerLineOverrides(cartItem, line);
  } catch {
    return null;
  }
}

/**
 * When the catalog request fails, still materialize the line so merge + UI do not drop
 * server rows (and we never treat empty local after GET as an intentional clear).
 */
export function stubCartProductFromServerLine(
  line: ServerCartLine,
  language: string,
): ICartProduct {
  const item: ICartProduct = {
    id: line.sku,
    sku: line.sku,
    product_id: line.product_id,
    name: '',
    slug: '',
    sale_price: 0,
    calculated_sale_price: 0,
    product_prices: [],
    quantity: line.quantity,
    language,
    cart_line_updated_at: line.last_modified_at,
    category: '',
    brand: '',
  };
  return applyServerLineOverrides(item, line);
}

/**
 * Build a full cart line like add-to-cart: Typesense first (same index as price/stock services),
 * then REST catalog + channel prices, then stub.
 */
export async function hydrateServerCartLine(
  line: ServerCartLine,
  language: string,
): Promise<ICartProduct | null> {
  const fromTs = await hydrateFromTypesense(line, language);
  if (fromTs) return fromTs;

  const fromRest = await hydrateFromRestCatalog(line, language);
  if (fromRest) return fromRest;

  return null;
}
